import { IMessageProps } from 'Components/Core';
import { HorizontalGrid } from 'Components/Layout';
import { IGridData } from 'Components/Layout/Grid/types';
import { pipe } from 'Lib/Hooks/Selectors';
import { joinPaths } from 'Lib/Object/Path/shared';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { WorksheetRecordGrid } from '.';
import * as oPath from '../../../Lib/Object/Path/Immutable';
import deepEquals from '../../../Lib/Object/deepEquals';
import { toTitleCase } from '../../../Lib/String';
import { withInjectedProps } from '../../Core/InjectedPropsContext';
import { GridFieldSection } from '../../Layout/FieldSection';
import { getMessagesForMultiplePaths } from './Helpers/messageHelpers';
import { IWorksheetComponent } from './types';

interface IWorksheetGridFieldSection extends IWorksheetComponent {
  heading?: string;
  visible?: boolean;
  name: string;
}

/**
 * Provides worksheets-specific functionality for GridFieldSections.
 *
 * It currently only does one thing: retrieve messages to show for the fields in the grid inside of it.
 */
const WorksheetGridFieldSectionBase: React.FC<React.PropsWithChildren<IWorksheetGridFieldSection>> = ({
  children,
  heading,
  visible = true,
  name,
  createWorksheetSelector,
  messages = [], // messages on the collection property
}) => {
  if (heading) {
    heading = toTitleCase(heading);
  }

  const [gridMessages, setGridMessages] = useState<IMessageProps[]>([]);

  // TODO: Fix this. This is selecting way too much for what we need. See QP-4443.
  const gridData = useSelector(
    pipe(createWorksheetSelector!(name), (d) => oPath.set({}, name, d, true)),
    deepEquals
  );

  const isHorizontal =
    React.Children.map(children, (child) => {
      return (
        (child as React.ReactElement)?.type === HorizontalGrid ||
        ((child as React.ReactElement)?.type === WorksheetRecordGrid &&
          (child as React.ReactElement).props?.orientation === 'horizontal')
      );
    })?.some((v) => !!v) ?? false;
  const gridName = React.Children.map(
    children,
    (child) => (child as React.ReactElement)?.type === WorksheetRecordGrid && (child as React.ReactElement).props?.name
  )?.filter((v) => !!v)[0];

  // Take out any name property from a component in a grid, so we can load messages for it.
  const handleReceivedData = useCallback(
    (data: IGridData | undefined, hiddenRows: Array<number | null | undefined>) => {
      let paths: string[] = [];

      //In a horizontal grid, each record (e.g. a specimen) is in its own column, with each row for one field (e.g. wet mass).
      //In a vertical grid, a record is in a row, with each column for one field.
      //We're using the hidden rows option that can be passed into each grid to
      //also control showing messages specifically for that field.
      data?.forEach((record, recordIndex: number) => {
        if (!isHorizontal && hiddenRows?.includes(recordIndex)) {
          return;
        }
        record.forEach((field, fieldIndex: number) => {
          if (isHorizontal && hiddenRows?.includes(fieldIndex)) {
            return;
          }
          if (field?.props) {
            field.props.name && paths.push(field.props.name);
            field.props.additionalMessagePaths && paths.push(...field.props.additionalMessagePaths);
          }
        });
      });
      if (gridName) paths = paths.map((p) => joinPaths(gridName, p));

      const newMessages = getMessagesForMultiplePaths(gridData, paths);

      // Clear ids so we don't get duplicate message link targets. The grid inputs will have link targets.
      newMessages.forEach((m) => (m.id = undefined));
      if (!deepEquals(gridMessages, newMessages)) setGridMessages(newMessages);
    },
    [gridData, gridName, gridMessages, isHorizontal]
  );

  return (
    <GridFieldSection
      heading={heading}
      visible={visible}
      messages={[...messages, ...gridMessages]}
      onReceivedData={handleReceivedData}
    >
      {children}
    </GridFieldSection>
  );
};

const WorksheetGridFieldSection = withInjectedProps(WorksheetGridFieldSectionBase);

export { WorksheetGridFieldSection };
export default WorksheetGridFieldSection;
